import type React from "react"
import { useState } from "react"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Plus, X } from "lucide-react"

interface DynamicInputListProps {
  items: string[]
  onChange: (newItems: string[]) => void
  placeholder: string
}

export function DynamicInputList({ items, onChange, placeholder }: DynamicInputListProps) {
  const [newItem, setNewItem] = useState("")
  const [error, setError] = useState<string | null>(null)

  const handleInputChange = (index: number, value: string) => {
    const newItems = [...items]
    newItems[index] = value
    onChange(newItems)
  }

  const handleAddItem = () => {
    if (!newItem.trim()) {
      setError("Please enter a value")
      return
    }
    setError(null)
    onChange([...items, newItem.trim()])
    setNewItem("")
  }

  const handleRemoveItem = (index: number) => {
    const newItems = items.filter((_, i) => i !== index)
    onChange(newItems)
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault()
      handleAddItem()
    }
  }

  return (
    <div className="space-y-2">
      {items.map((item, index) => (
        <div key={index} className="flex items-center space-x-2">
          <Input
            value={item}
            onChange={(e) => handleInputChange(index, e.target.value)}
            placeholder={placeholder}
            className={item.trim() === "" ? "border-red-500" : ""}
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => handleRemoveItem(index)}
            aria-label={`Remove item ${index + 1}`}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      ))}
      <div className="space-y-1">
        <div className="flex items-center space-x-2">
          <Input
            value={newItem}
            onChange={(e) => {
              setNewItem(e.target.value)
              setError(null)
            }}
            placeholder={`Add ${placeholder}`}
            onKeyPress={handleKeyPress}
            className={error ? "border-red-500" : ""}
          />
          <Button type="button" variant="outline" size="icon" onClick={handleAddItem} aria-label="Add new item">
            <Plus className="h-4 w-4" />
          </Button>
        </div>
        {error && <p className="text-sm text-red-500">{error}</p>}
      </div>
    </div>
  )
}

